import { useState } from "react";
import axios from "axios";
import { Send, Mail } from "lucide-react";
import { FadeIn, SectionHeader } from "../components/Motion";
import { toast } from "../components/ui/sonner";

const EMAIL = "hello@shelfsensehq.com";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message || "Message sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || `Something went wrong — email us directly at ${EMAIL}.`);
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 text-slate-100 placeholder:text-slate-600 focus:border-green-400/60 focus:outline-none focus:ring-2 focus:ring-green-400/20 transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 sm:py-36">
      <div className="pointer-events-none absolute bottom-0 left-1/2 h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-purple-600/12 blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2">
          <div>
            <SectionHeader
              index="06"
              label="Get in Touch"
              title="Tell us about your product"
              lead="Whether you're launching a new product or reviewing an existing shelf life — we'd love to hear from you."
            />
            <FadeIn delay={0.25}>
              <div className="mt-10 space-y-5">
                <p className="text-base text-slate-400 sm:text-lg">
                  Fill in the form, or email us directly at
                </p>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="contact-email-link"
                  className="group inline-flex items-center gap-3 rounded-full border border-green-400/40 px-6 py-3.5 font-mono text-sm text-green-300 transition-colors duration-300 hover:bg-green-400/10"
                >
                  <Mail className="h-4 w-4" />
                  {EMAIL}
                </a>
                <p className="max-w-md text-sm leading-relaxed text-slate-500">
                  Every enquiry is read by a food scientist — not a bot. Tell us about your product, its packaging, and where it's stored, and we'll point you in the right direction.
                </p>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="glass spotlight-frame rounded-[2rem] p-8 sm:p-10"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                    Name *
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    className={field}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                    Email *
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    data-testid="contact-email-input"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@company.com"
                    className={field}
                  />
                </div>
              </div>
              <div className="mt-5">
                <label htmlFor="contact-company" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                  Company
                </label>
                <input
                  id="contact-company"
                  data-testid="contact-company-input"
                  value={form.company}
                  onChange={set("company")}
                  placeholder="Brand or manufacturer"
                  className={field}
                />
              </div>
              <div className="mt-5">
                <label htmlFor="contact-message" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                  Message *
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us about your product, its packaging, and your storage conditions…"
                  className={`${field} resize-none`}
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit-button"
                className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-purple-600 px-7 py-4 text-base font-semibold text-white shadow-lg shadow-purple-950/50 transition-all duration-300 hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send message"}
                <Send className={`h-4 w-4 transition-transform duration-300 ${sending ? "" : "group-hover:translate-x-1 group-hover:-translate-y-0.5"}`} />
              </button>
            </form>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

export default Contact;
